import { useReconciliationEntitled } from '@/modules/account/hooks'
import { cn, numberToCurrency } from '@/utils'
import { monthLabel, monthShortLabel } from '../model/months'
import type { AccountAsOf } from '../model/portfolioOverview'
import {
  cashSourceNote,
  longDate,
  securitiesSourceNote,
} from '../portfolioText'
import { ReasonActionButton } from '../ReasonActionButton'
import { gapSentence } from './gapSentence'

const FigurePart = ({
  label,
  value,
  note,
}: {
  label: string
  value: number
  note: string | null
}) => (
  <div>
    <div className="text-xs text-ink-subtle">{label}</div>
    <div className="mt-0.5 whitespace-nowrap tabular-nums">
      {numberToCurrency(value)}
      {note && <span className="ml-2 text-xs text-ink-subtle">{note}</span>}
    </div>
  </div>
)

const NoCash = ({ account }: { account: AccountAsOf }) => (
  <p className="self-end text-xs text-ink-subtle">
    {noCashSentence(account, useReconciliationEntitled())}
  </p>
)

const NoValue = ({ account }: { account: AccountAsOf }) => (
  <>
    <p className="mt-2 text-xs text-ink-subtle">{gapSentence(account)}.</p>
    {account.status === 'missing' && (
      <div className="mt-3">
        <ReasonActionButton
          action={{
            kind: 'upload_statement',
            label: `Upload the ${monthShortLabel(account.month)} statement`,
            month: account.month,
          }}
        />
      </div>
    )}
  </>
)

// Without the reconcile feature lukita withholds every cash balance, so the plan is the reason.
const noCashSentence = (account: AccountAsOf, isCashInPlan: boolean) =>
  isCashInPlan
    ? `Cash balance not captured on the ${monthLabel(account.month)} statement.`
    : 'Cash balances are not part of this plan.'

const ValueParts = ({ account }: { account: AccountAsOf }) => (
  <>
    <div className="mt-5 flex flex-wrap gap-x-12 gap-y-3">
      <FigurePart
        label="Securities"
        value={account.securities ?? 0}
        note={securitiesSourceNote(account)}
      />
      {account.cash == null ? (
        <NoCash account={account} />
      ) : (
        <FigurePart
          label="Cash"
          value={account.cash}
          note={cashSourceNote(account)}
        />
      )}
    </div>
    <div className="mt-5 flex items-baseline gap-2">
      {account.change ? (
        <>
          <span className="text-sm text-ink-subtle">
            Change since {longDate(account.change.since)}
          </span>
          <span className="tabular-nums">
            {numberToCurrency(account.change.amount)}
          </span>
        </>
      ) : (
        <span className="text-sm text-ink-subtle">No earlier statement</span>
      )}
    </div>
  </>
)

const valueLabel = (account: AccountAsOf, asOfDate: string) =>
  account.cash == null
    ? `Securities on ${longDate(asOfDate)}`
    : `Securities and cash on ${longDate(asOfDate)}`

// The chosen account's value on the As-of date: securities plus cash, each said where it came from.
export const AccountFigure = ({
  account,
  asOfDate,
}: {
  account: AccountAsOf
  asOfDate: string
}) => (
  <div>
    <div className="text-xs text-ink-subtle">
      {valueLabel(account, asOfDate)}
    </div>
    <div
      className={cn(
        'mt-0.5 text-[30px] leading-[38px] font-semibold tracking-tight tabular-nums',
        account.value == null && 'text-ink-subtle',
      )}
    >
      {account.value == null ? 'No value' : numberToCurrency(account.value)}
    </div>
    {account.value == null ? (
      <NoValue account={account} />
    ) : (
      <ValueParts account={account} />
    )}
  </div>
)
